import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

interface AdSenseProps {
  client: string;
  slot: string;
  format?: string;
  responsive?: string;
  width?: string | number;
  height?: string | number;
}

const AdSense = ({
  client,
  slot,
  format = 'auto',
  responsive = 'true',
  width = '100%',
  height = 'auto',
}: AdSenseProps) => {
  const adRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [adLoadFailed, setAdLoadFailed] = useState(false);
  const isDevelopment = process.env.NODE_ENV === 'development';

  useEffect(() => {
    // Create an observer to check when the ad container is visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Check if element is visible and has width
        if (entry.isIntersecting && entry.boundingClientRect.width > 0) {
          setIsVisible(true);
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.1 // Trigger when at least 10% of the element is visible
    });
    
    if (adRef.current) {
      observer.observe(adRef.current);
    }
    
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    // Only push the ad when component is visible with dimensions and in production
    if (isVisible && !isDevelopment) {
      try {
        // Use a small timeout to ensure the DOM is fully rendered
        const timeoutId = setTimeout(() => {
          const adsbygoogle = window.adsbygoogle || [];
          adsbygoogle.push({});
        }, 100);
        
        // Set a timeout to check if ad loaded successfully
        const checkAdTimeout = setTimeout(() => {
          if (adRef.current) {
            const adElement = adRef.current.querySelector('ins.adsbygoogle');
            // Check if ad element exists and has been filled
            if (adElement && adElement.getAttribute('data-ad-status') !== 'filled') {
              setAdLoadFailed(true);
            }
          }
        }, 3000); // Wait 3 seconds to check if ad loaded
        
        return () => {
          clearTimeout(timeoutId);
          clearTimeout(checkAdTimeout);
        };
      } catch (e) {
        console.error('AdSense error:', e);
        setAdLoadFailed(true);
      }
    }
  }, [isVisible, isDevelopment]);

  // Modified container style to better handle responsive sizing
  const containerStyle = {
    display: 'block',
    width: '100%', // Always use 100% width for the container
    maxWidth: width !== 'auto' && typeof width === 'string' && width.includes('%') ? width : '100%',
    maxHeight: height !== 'auto' ? height : undefined,
    // Only add these constraints for non-responsive ads
    ...(responsive !== 'true' && {
      minWidth: width !== 'auto' ? width : '300px',
      minHeight: height !== 'auto' ? height : '100px'
    })
  };

  // Simple development placeholder
  if (isDevelopment) {
    return (
      <Box 
        ref={adRef}
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          width: '100%',
          border: '1px dashed #ccc',
          borderRadius: '4px',
          background: '#f8f8f8',
          minHeight: '100px',
          color: '#666',
          fontSize: '14px'
        }}
      >
        AdSense Ad (slot: {slot}) - Only visible in production
      </Box>
    );
  }

  // Show placeholder if ad failed to load in production
  if (adLoadFailed) {
    return (
      <Box 
        ref={adRef}
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          width: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          background: '#f5f5f5',
          minHeight: '100px',
          color: '#999',
          fontSize: '14px',
          padding: '20px',
          textAlign: 'center'
        }}
      >
        Advertisement Space
      </Box>
    );
  }

  return (
    <Box 
      ref={adRef}
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        width: '100%'
      }}
    >
      <ins
        className="adsbygoogle"
        style={containerStyle}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive}
      />
    </Box>
  );
};

export default AdSense;

// Add this to global.d.ts or similar types file
declare global {
  interface Window {
    adsbygoogle: any[];
  }
}